<template>
  <div class="modal share">
    <article>
      <h1>{{ $t("dashboard-modal-share-files") }}</h1>
      <form @keydown.enter.prevent>
        <fieldset>
          <div class="form-group">
            <label>
              {{ $t("dashboard-modal-form-email-label") }}
              <p v-if="!recipientEmailsValid" class="error-description">
                {{ $t("dashboard-modal-invalid-email") }}
              </p>
              <div
                :class="[
                  'tags-input-wrapper',
                  { error: !recipientEmailsValid }
                ]"
              >
                <vue-tags-input
                  v-model="emailTag"
                  :tags.sync="recipientEmails"
                  :placeholder="$t('dashboard-modal-emails-placeholder')"
                  :autocomplete-items="filteredParticipants"
                  :validation="emailTagValidators"
                  @tags-changed="updateTags"
                >
                  <div
                    slot="tag-left"
                    slot-scope="props"
                    class="tooltip"
                    :tooltip-title="props.tag.text"
                  ></div>
                  <div
                    slot="tag-actions"
                    slot-scope="props"
                    @click="props.performDelete(props.index)"
                  >
                    <span class="close-icon-holder">
                      <close-icon
                        width="6px"
                        height="8px"
                        weight="light"
                      ></close-icon>
                    </span>
                  </div>
                </vue-tags-input>
                <error-icon
                  v-if="!recipientEmailsValid"
                  fillColor="#ff4d4d"
                  width="18"
                  height="18"
                ></error-icon>
              </div>
            </label>
          </div>
          <div class="form-group">
            <textarea
              rows="3"
              name="message"
              autocomplete="new-message"
              maxlength="1000"
              v-model.trim="message"
              :placeholder="$t('dashboard-modal-message-placeholder')"
            ></textarea>
          </div>
          <div class="form-group date">
            <div>
              <clock-icon width="13" height="13"></clock-icon>
              {{ $t("dashboard-modal-form-expires-label") }}
            </div>
            <div>
              <label>
                {{ $t("dashboard-modal-form-at") }}
                <select @change="setDate($event)">
                  <option
                    :selected="time.timeInMinutes === nearestTime"
                    v-for="time in dateTimes"
                    :value="time.timeInMinutes"
                    :key="time.timeInMinutes"
                  >
                    {{ time.label }}
                  </option>
                </select>
              </label>
            </div>
            <div class="datepicker-wrapper">
              <span>{{ $t("dashboard-modal-form-on") }}</span>
              <datepicker
                v-model="validUntilModel"
                :language="languages[locale]"
                :format="datePickerFormat"
                :disabled-dates="disabledDates"
              ></datepicker>
            </div>
          </div>
          <div class="form-group right">
            <div
              v-for="date in predefinedDates"
              :key="date.label"
              class="radio-button-wrapper"
            >
              <input
                type="radio"
                :id="date.timeInMinutes"
                :value="date.timeInMinutes"
                v-model="validUntilModel"
              />
              <label :for="date.timeInMinutes">{{ date.label }}</label>
            </div>
          </div>
          <div class="form-group">
            <label class="checkbox-wrapper">
              <input type="checkbox" v-model="passwordProtected" />
              <span class="checkbox-button"></span>
              {{ $t("password-protect") }}
              <a
                class="tooltip"
                :tooltip-title="$t('password-protect-tooltip')"
                >{{ $t("whats-this") }}</a
              >
            </label>
          </div>
          <footer class="form-group">
            <button class="default" @click="closeModalclearExchangeDraft">
              <close-icon width="15px" height="15px"></close-icon>
              {{ $t("dashboard-modal-cancel") }}
            </button>
            <button
              type="submit"
              class="primary"
              :disabled="
                !recipientEmails.length ||
                  !validUntil ||
                  !recipientEmailsValid ||
                  filesNotChoosen ||
                  failedUploads
              "
              @click.prevent="handleShare"
            >
              <share-icon width="20" height="15"></share-icon>
              {{ $t("dashboard-modal-share") }}
            </button>
          </footer>
        </fieldset>
      </form>
    </article>
    <aside>
      <div class="form-group">
        <h3>{{ $t("dashboard-modal-files-title") }}</h3>
        <div v-if="exchangeCopyFilesExist" class="files-to-upload">
          <exchange-copy-file
            v-for="(file, index) in exchangeCopyChoosenFiles"
            :key="index"
            :file="file"
            :index="index"
            :exchangeCopyId="exchangeCopy.id"
            @removeFileFromExchangeCopy="removeFileFromExchangeCopy"
          ></exchange-copy-file>
        </div>
        <div v-if="choosenFiles.length" class="files-to-upload">
          <exchange-file
            v-for="(file, index) in choosenFiles"
            :key="index"
            :file="file"
            :index="index"
            @increaseFailedUploadsCounter="increaseFailedUploadsCounter"
            @decreaseFailedUploadsCounter="decreaseFailedUploadsCounter"
            @removeFile="removeFile"
          ></exchange-file>
        </div>
      </div>
      <div class="form-group">
        <small class="upload-file-description">{{
          $t("upload-modal-add-files-description")
        }}</small>
        <label class="upload-file-wrapper default">
          + {{ $t("upload-modal-add-files") }}
          <input
            type="file"
            id="files"
            ref="files"
            multiple
            v-on:change="handleFilesUpload()"
          />
        </label>
      </div>
    </aside>
  </div>
</template>

<script>
import moment from "moment";
import i18n from "@/i18n";
import * as lang from "@/locales/datepicker-locales/locale";
import Datepicker from "vuejs-datepicker";
import { VueTagsInput, createTags } from "@johmun/vue-tags-input";
import { mapState, mapActions } from "vuex";
import CloseIcon from "@/components/icons/CloseIcon";
import ClockIcon from "@/components/icons/ClockIcon";
import ShareIcon from "@/components/icons/ShareIcon";
import ErrorIcon from "@/components/icons/ErrorIcon";
import ExchangeFile from "./ExchangeFile";
import ExchangeCopyFile from "./ExchangeCopyFile";

export default {
  name: "Share",
  props: {
    exchangeCopy: {
      required: true
    }
  },
  components: {
    ExchangeFile,
    ExchangeCopyFile,
    CloseIcon,
    ShareIcon,
    ClockIcon,
    Datepicker,
    ErrorIcon,
    VueTagsInput
  },
  data() {
    return {
      recipientEmails: [],
      message: (this.exchangeCopy && this.exchangeCopy.message) || null,
      passwordProtected:
        (this.exchangeCopy && this.exchangeCopy.encrypted) || false,
      validUntilDateTime: "",
      recipientEmailTags: [],
      recipientEmailsValid: true,
      emailTag: "",
      predefinedDates: [
        {
          label: `24 ${this.$t("dashboard-modal-form-hours")}`,
          timeInMinutes: moment()
            .add(1, "days")
            .seconds(0)
            .milliseconds(0)
            .toDate()
        },
        {
          label: `48 ${this.$t("dashboard-modal-form-hours")}`,
          timeInMinutes: moment()
            .add(2, "days")
            .seconds(0)
            .milliseconds(0)
            .toDate()
        },
        {
          label: `7 ${this.$t("dashboard-modal-form-days")}`,
          timeInMinutes: moment()
            .add(7, "days")
            .seconds(0)
            .milliseconds(0)
            .toDate()
        },
        {
          label: `30 ${this.$t("dashboard-modal-form-days")}`,
          timeInMinutes: moment()
            .add(30, "days")
            .seconds(0)
            .milliseconds(0)
            .toDate()
        }
      ],
      languages: lang,
      validUntil: moment()
        .add(1, "days")
        .seconds(0)
        .milliseconds(0),
      validUntilModel: moment()
        .add(1, "days")
        .seconds(0)
        .milliseconds(0)
        .toDate(),
      disabledDates: {
        to: moment()
          .subtract(1, "days")
          .toDate(),
        from: moment()
          .add(30, "days")
          .toDate()
      },
      invalidEmailDomains: [],
      emailTagValidators: [
        {
          classes: "validator",
          rule: /^(.+@.+)$/
        },
        {
          classes: "validator",
          rule: ({ text }) => this.invalidEmailDomains.indexOf(text) !== -1
        }
      ],
      failedUploadsCount: 0
    };
  },
  computed: {
    ...mapState("auth", ["user"]),
    ...mapState("exchange", [
      "participants",
      "choosenFiles",
      "exchangeCopyChoosenFiles",
      "exchange",
      "exchangeId",
      "dateTimes"
    ]),
    exchangeCopyFilesExist() {
      return (
        this.exchangeCopyChoosenFiles.length &&
        this.exchangeCopy &&
        this.exchangeCopyChoosenFiles.some(
          item => item && item.filename !== null
        )
      );
    },
    filesNotChoosen() {
      return (
        (!this.choosenFiles.length ||
          !this.choosenFiles.some(file => file && file.name)) &&
        !this.exchangeCopyFilesExist
      );
    },
    failedUploads() {
      return this.failedUploadsCount > 0;
    },
    filteredParticipants() {
      return this.participants.filter(email => {
        return (
          email.text
            .substring(0, this.emailTag.length)
            .toLowerCase()
            .indexOf(this.emailTag.toLowerCase()) !== -1
        );
      });
    },
    locale() {
      return i18n.locale;
    },
    datePickerFormat() {
      return this.$t("date-picker-format");
    },
    nearestTime() {
      const currentDateTime =
        this.validUntil.hours() * 60 + this.validUntil.minutes();
      return this.dateTimes.reduce((prev, curr) => {
        return Math.abs(curr.timeInMinutes - currentDateTime) <
          Math.abs(prev - currentDateTime)
          ? curr.timeInMinutes
          : prev;
      }, 0);
    }
  },
  methods: {
    ...mapActions("common", ["setLoading", "showModal"]),
    ...mapActions("exchange", [
      "getParticipantList",
      "deleteChoosenFile",
      "deleteFile",
      "clearExchangeDraftFromState",
      "deleteChoosenFiles",
      "deleteExchangeCopyChoosenFile"
    ]),
    handleFilesUpload() {
      this.$emit("handleFilesUpload", this.$refs.files.files);
    },
    closeUploadModal() {
      this.$emit("closeUploadModal");
    },
    closeModalclearExchangeDraft() {
      this.$emit("closeModalclearExchangeDraft");
    },
    handleShare() {
      let dateTime = moment(this.validUntilModel)
        .hours(0)
        .minutes(0)
        .seconds(0)
        .milliseconds(0)
        .add(this.validUntilDateTime || this.nearestTime, "minutes")
        .valueOf();

      let data = {
        id: this.exchangeId,
        formData: {
          recipientEmails: this.recipientEmails.map(
            recipient => recipient.text
          ),
          message: this.message,
          validUntil: dateTime,
          encrypted: this.passwordProtected
        }
      };

      this.setLoading(true);
      this.$store
        .dispatch("exchange/updateExchange", data)
        .then(() => {
          this.$store
            .dispatch("exchange/sendExchange", this.exchangeId)
            .then(() => {
              if (!this.exchangeCopy) {
                this.clearExchangeDraftFromState(this.exchangeId);
              }
              this.deleteChoosenFiles();
              this.closeUploadModal();
            })
            .catch(err => {
              if (
                err.hasOwnProperty("unresolvedEmails") &&
                err.unresolvedEmails.length
              ) {
                this.resolveEmails(err.unresolvedEmails);
              }
            });
        })
        .catch(err => {
          this.showModal({
            title: err.errorCode
          });
        })
        .finally(() => {
          this.$emit("clearExchangeCopy");
          this.setLoading(false);
        });
    },
    increaseFailedUploadsCounter() {
      this.failedUploadsCount += 1;
    },
    decreaseFailedUploadsCounter() {
      this.failedUploadsCount -= 1;
    },
    updateTags(emailTags) {
      let newTags = [];
      emailTags.map(emailTag => {
        this.recipientEmailTags.push(emailTag);
        newTags.push({ text: emailTag.text });
      });
      this.recipientEmails = newTags;

      this.recipientEmailsValid = !emailTags.some(emailTag => {
        return emailTag.tiClasses.includes("ti-invalid");
      });
    },
    resolveEmails(emails) {
      this.invalidEmailDomains = emails;
      const validatedEmails = createTags(emails, this.emailTagValidators);

      const validEmails = this.recipientEmails.filter(email => {
        return !emails.includes(email.text);
      });

      this.recipientEmails = [...validEmails, ...validatedEmails];
      this.updateTags(this.recipientEmails);
    },
    setDate(e) {
      if (e.target && e.target.value) {
        this.validUntilDateTime = e.target.value;
      }
    },
    removeFile(payload) {
      this.setLoading(true);

      const promises = [this.deleteChoosenFile(payload.index)];
      if (payload && payload.fileId) {
        promises.push(this.deleteFile(payload));
      }

      Promise.all(promises)
        .then(() => this.setLoading(false))
        .catch(err => {
          this.setLoading(false);
          this.showModal({
            title: err.errorCode
          });
        });
    },
    removeFileFromExchangeCopy(payload) {
      this.setLoading(true);
      this.deleteFile(payload)
        .then(res => {
          this.setLoading(false);
          if (res && res.status === 200) {
            this.deleteExchangeCopyChoosenFile(payload.index);
          }
        })
        .catch(err => {
          this.setLoading(false);
          this.showModal({
            title: err.errorCode
          });
        });
    }
  },
  mounted() {
    if (this.exchangeCopy && this.exchangeCopy.receivers) {
      let newTags = [];
      this.exchangeCopy.receivers.map(emailTag => {
        this.recipientEmailTags.push(emailTag);
        newTags.push({ text: emailTag.email });
      });
      this.recipientEmails = newTags;
    }
  }
};
</script>

<style lang="scss">
@import "../../../../../assets/styles/tooltip";
</style>

<style scoped lang="scss">
@import "../../../../../assets/styles/variables";

.modal {
  display: flex;
  &.share {
    min-width: 800px;
    max-width: 900px;
    background: white;
    border: 1px solid $middle-gray;
    max-height: 80%;
    .tooltip:before {
      width: 280px;
    }
    aside,
    article {
      padding: 30px;
      text-align: left;
    }
    article {
      width: 65%;
      border-bottom-left-radius: 16px;
      border-top-left-radius: 16px;
    }
    aside {
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      overflow: auto;
      width: 35%;
      background: $light-gray;
      box-shadow: inset 6px 0 5px -5px rgba(0, 0, 0, 0.15);
      border-bottom-right-radius: 16px;
      border-top-right-radius: 16px;
    }
    label {
      font-size: 14px;
      &:not(.upload-file-wrapper) {
        font-family: Gotham-book;
      }
      &.upload-file-wrapper {
        font-family: Gotham-medium;
      }
    }
    form {
      footer {
        display: flex;
        justify-content: space-between;
        margin-top: 20px;
      }
      .form-group.date {
        display: flex;
        align-items: center;
        > div {
          &:not(:last-child) {
            margin-right: 10px;
          }
          &:last-child {
            flex: 2;
            .vdp-datepicker {
              width: 100%;
            }
          }
          select,
          .vdp-datepicker {
            margin-left: 10px;
          }
        }
        label {
          display: flex;
          align-items: center;
          > span {
            margin: 15px 15px 0 0;
            flex-shrink: 0;
          }
        }
        .datepicker-wrapper {
          display: flex;
          align-items: center;
        }
      }
    }
    .files-to-upload {
      padding-right: 10px;
      .file {
        display: flex;
        font-size: 14px;
        margin-bottom: 10px;
        svg {
          display: inline-block;
          vertical-align: middle;
          margin-right: 8px;
          flex-shrink: 0;
        }
        .file-description {
          text-overflow: ellipsis;
          white-space: nowrap;
          overflow: hidden;
        }
      }
      small {
        color: $placeholder-color;
      }
    }
  }
  /deep/ .close-icon-holder {
    width: 14px;
    height: 14px;
    border-radius: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    margin-left: 2px;
    cursor: pointer;
    transition: background 0.3s ease;
  }

  .ti-valid {
    .close-icon-holder {
      background: $middle-gray;
      border: 1px solid #979797;
      &:hover {
        background: darken($middle-gray, 5%);
      }
    }
  }

  .ti-invalid {
    .close-icon-holder {
      border: 1px solid $error-color;
    }
  }

  .upload-file-description {
    color: $placeholder-color;
    padding-bottom: 10px;
    display: inline-block;
  }
}
</style>
